import {
  BasePaginationProps,
  ConfirmationPrompt,
  ErrorBoundary,
  LoadingSpinner,
} from '@jupiterone/jupiter-web-toolkit';
import React, { useEffect, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import GraphViewerContainer from './components/GraphViewerContainer';
import { GraphEdge, GraphVertex, GraphViewType } from './components/types';
import {
  GraphNetworkContext,
  useNetworkContextState,
} from './hooks/useGraphNetwork';
import useSavedNodePositions from './hooks/useSavedNodePositions';

export interface GraphViewerAppProps {
  vertices: GraphVertex[];
  edges?: GraphEdge[];
  mode?: GraphViewType;
  fullScreen?: boolean;
  query?: string;
  onUpdateGraph?: VoidFunction;
  initiallySelectedNode?: GraphVertex;
  paginationProps?: BasePaginationProps;
}

const GraphViewerApp = (props: GraphViewerAppProps) => {
  const { query } = props;
  const networkState = useNetworkContextState();
  const [internalGraphType, setInternalGraphType] = useState<GraphViewType>(
    props.mode || GraphViewType.mesh
  );
  const { positions, loading, onUpdateNodePositions } = useSavedNodePositions(
    query,
    internalGraphType
  );
  const promptOpen = useBooleanState(false);

  const handleGraphUpdate = () => props.onUpdateGraph && promptOpen.setTrue();

  useEffect(() => {
    if (props.mode) {
      setInternalGraphType(props.mode);
    }
  }, [props.mode]);

  return query && loading ? (
    <LoadingSpinner />
  ) : (
    <>
      <GraphNetworkContext.Provider value={networkState}>
        <GraphViewerContainer
          {...props}
          graphType={internalGraphType}
          onUpdateNodePositions={onUpdateNodePositions}
          nodePositions={positions}
          onUpdateGraph={handleGraphUpdate}
          onUpdateGraphType={setInternalGraphType}
        />
      </GraphNetworkContext.Provider>
      {props.onUpdateGraph && (
        <ConfirmationPrompt
          open={promptOpen.state}
          message={'The graph has been updated, would you like to reload it?'}
          onConfirm={props.onUpdateGraph}
          onClose={promptOpen.setFalse}
          confirmButtonText={'Reload'}
        />
      )}
    </>
  );
};

const WrappedGraphViewerApp = (props: GraphViewerAppProps) => (
  <ErrorBoundary>
    <GraphViewerApp {...props} />
  </ErrorBoundary>
);

export default WrappedGraphViewerApp;
